import {
  Button,
  Icon,
  IconName,
  IconTile,
  List,
  ListItem,
  Tag,
  Text,
} from '@pm/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { usePathWithParams } from '../../../hooks/navigation/use-path-with-params';
import { usePatientProfile } from '../../../hooks/profile';
import { getAdditionalConditionFlowLink } from './additional_condition_card_utils';
import type { AdditionalConditionName } from './types';

const CliniciansAvatarStack = () => (
  <div className="flex items-center">
    <img
      src="/images/clinicians/clinician_3.svg"
      className="relative z-30 -mr-1 h-4 w-4 rounded-full object-cover border-1 border-white"
      alt=""
    />
    <img
      src="/images/clinicians/clinician_2.svg"
      className="relative z-20 -mr-1 h-4 w-4 rounded-full object-cover border-1 border-white"
      alt=""
    />
    <img
      src="/images/clinicians/clinician_1.svg"
      className="relative z-10 h-4 w-4 rounded-full object-cover border-1 border-white"
      alt=""
    />
  </div>
);

export const AdditionalConditionCoverPage = () => {
  const { t } = useTranslation('treatments', {
    keyPrefix: 'AdditionalTreatmentsList',
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const buildPathWithParams = usePathWithParams();
  const { isUsaPatient } = usePatientProfile();

  const conditionName = searchParams.get(
    'treatment',
  ) as AdditionalConditionName | null;
  const action = searchParams.get('action') as 'intake' | 'refill' | null;
  const isRefill = action === 'refill';
  const showEdCostCopy = conditionName === 'ed' && isUsaPatient;

  const flowLink = conditionName
    ? getAdditionalConditionFlowLink(conditionName, 'prep', isRefill)
    : undefined;

  type CoverPageStep = {
    number: number;
    title: string;
    description: string;
    pillText?: string;
    iconName: IconName;
  };
  const steps: CoverPageStep[] = [
    {
      number: 1,
      title: t('CoverPageItems.Step1.Title'),
      description: isRefill
        ? t('CoverPageItems.Step1.DescriptionRefill')
        : t('CoverPageItems.Step1.Description'),
      pillText: t('CoverPageItems.Step1.PillText'),
      iconName: 'assignment',
    },
    {
      number: 2,
      title: t('CoverPageItems.Step2.Title'),
      description: isRefill
        ? t('CoverPageItems.Step2.DescriptionRefill')
        : t('CoverPageItems.Step2.Description'),
      pillText: t('CoverPageItems.Step2.PillText'),
      iconName: 'patient_list',
    },
    {
      number: 3,
      title: t('CoverPageItems.Step3.Title'),
      description: showEdCostCopy
        ? t('Treatments.ed.CoverPageCostText')
        : t('CoverPageItems.Step3.Description'),
      iconName: 'pill',
    },
    {
      number: 4,
      title: t('CoverPageItems.Step4.Title'),
      description: t('CoverPageItems.Step4.Description'),
      pillText: t('CoverPageItems.Step4.PillText'),
      iconName: 'local_shipping',
    },
  ];

  return (
    <div className="flex w-full justify-center">
      <div className="max-w-[420px] space-y-s">
        <div className="flex items-center mb-l w-full bg-grey-300 rounded-m overflow-hidden h-auto aspect-[16/6.5]">
          <img
            className="object-cover object-[center_-5px]"
            src={`/images/additional-treatments/Intake-cover-${conditionName}.png`}
            alt={'additional_treatments'}
          />
        </div>
        <div>
          <Text size="labelStrong" color="contentPositive">
            {isRefill ? t('CoverPageIntroTextRefill') : t('CoverPageIntroText')}
          </Text>
          <Text size="headingM" className="pt-xs">
            {conditionName &&
              t(
                `Treatments.${conditionName}.${
                  isRefill ? 'CoverPageTitleRefill' : 'CoverPageTitle'
                }`,
              )}
          </Text>
          <div className="flex gap-xs pt-xs">
            <Tag
              size="small"
              intent={'surface'}
              leadingIcon={<Icon name="schedule" size={'small'} />}
            >
              {isRefill ? t('TimeToDoRefill') : t('TimeToDoIntake')}
            </Tag>

            <Tag
              size="small"
              intent={'surface'}
              leadingIcon={<CliniciansAvatarStack />}
            >
              {t('ExpertClinicalReview')}
            </Tag>
          </div>
        </div>
        <div className="[&_li]:first:pt-1 [&_li]:last:pb-1">
          <List interactionType="static" grouping="noSeparators">
            {steps.map((step) => (
              <ListItem
                key={step.number}
                heading={step.title}
                body={step.description}
                headingTag={step.pillText}
                leadingElement={
                  <IconTile
                    icon={step.iconName}
                    intent="accent"
                    iconColor="default"
                  />
                }
              />
            ))}
          </List>
        </div>
        {showEdCostCopy && (
          <Text size="bodyS" color="contentSubdued">
            {t('Treatments.ed.CoverPageCostDisclaimer')}
          </Text>
        )}
        <div className="flex w-full justify-center">
          <div className="w-full">
            <Button
              type="button"
              width="full"
              onClick={() =>
                navigate(buildPathWithParams({ to: flowLink ?? '/' }))
              }
            >
              {isRefill
                ? t('CardStateCTAs.AvailableRefill')
                : t('CardStateCTAs.Available')}
              <Icon name="arrow_forward" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
